/**
 * Settings API client for user preferences and feature flags.
 */

import { z } from 'zod'
import { apiFetch } from './client'

/**
 * User settings schema
 */
export const UserSettingsSchema = z.object({
  display_name: z.string().nullable(),
  avatar_url: z.string().nullable(),
  ui_language: z.string(),
  response_language: z.string(),
  response_tone: z.string(),
  auto_detect_tourist_language: z.boolean(),
})

export type UserSettings = z.infer<typeof UserSettingsSchema>

/**
 * Feature flags schema
 */
export const FeatureFlagsSchema = z.object({
  response_automation: z.boolean(),
  review_request_automation: z.boolean(),
  trends_page: z.boolean(),
  // Per-user, unlike the flags above: mirrors `users.is_admin` server-side.
  admin_tools: z.boolean(),
})

export type FeatureFlags = z.infer<typeof FeatureFlagsSchema>

/**
 * Update settings request - all fields optional for partial updates
 */
export interface UpdateSettingsRequest {
  display_name?: string | null
  avatar_url?: string | null
  ui_language?: string
  response_language?: string
  response_tone?: string
  auto_detect_tourist_language?: boolean
}

/**
 * Fetch current user's settings.
 * Creates default settings if they don't exist.
 */
export async function fetchUserSettings(): Promise<UserSettings> {
  const data = await apiFetch<UserSettings>('/settings/me')
  return UserSettingsSchema.parse(data)
}

/**
 * Update current user's settings.
 * Only updates fields that are provided.
 */
export async function updateUserSettings(settings: UpdateSettingsRequest): Promise<UserSettings> {
  const data = await apiFetch<UserSettings>('/settings/me', {
    method: 'PATCH',
    body: JSON.stringify(settings),
  })
  return UserSettingsSchema.parse(data)
}

/**
 * Fetch feature flags for the current user.
 */
export async function fetchFeatureFlags(): Promise<FeatureFlags> {
  const data = await apiFetch<FeatureFlags>('/settings/features')
  return FeatureFlagsSchema.parse(data)
}
